/**
 * Tiny typed client for the ObsidianWeb backend.
 *
 * Mirrors `apps/server/src/routes/vault.ts` response shapes by hand. The
 * codegen step (`openapi-typescript`) lands with the OpenAPI spec in a later
 * task; until then this file is the source of truth on the client side.
 */

export interface TreeNode {
  readonly path: string;
  readonly name: string;
  readonly kind: 'file' | 'directory';
  readonly size: number;
  readonly mtimeMs: number;
  readonly children?: readonly TreeNode[];
}

export interface MarkdownFilePayload {
  readonly kind: 'markdown';
  readonly path: string;
  readonly raw: string;
  readonly html: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

export interface TextFilePayload {
  readonly kind: 'text';
  readonly path: string;
  readonly content: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

/** Scene shape returned for `.excalidraw` / `.excalidraw.md` files. Mirrors
 *  `ExcalidrawSceneDto` in `apps/server/src/routes/vault.ts`. */
export interface ExcalidrawSceneDto {
  readonly version: number;
  readonly elements: readonly unknown[];
  readonly appState: Readonly<Record<string, unknown>>;
  readonly files: Readonly<Record<string, unknown>>;
  readonly source: 'excalidraw' | 'obsidian-md';
  readonly raw: Readonly<Record<string, unknown>>;
}

export interface ExcalidrawFilePayload {
  readonly kind: 'excalidraw';
  readonly path: string;
  readonly scene: ExcalidrawSceneDto;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

export interface ExcalidrawBrokenFilePayload {
  readonly kind: 'excalidraw-broken';
  readonly path: string;
  readonly raw: string;
  readonly errorCode: string;
  readonly errorMessage: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

export type FilePayload =
  MarkdownFilePayload | TextFilePayload | ExcalidrawFilePayload | ExcalidrawBrokenFilePayload;

export interface TreeResponse {
  readonly entries: readonly TreeNode[];
}

export interface ApiErrorBody {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly path?: string;
    /** Present on 401 UNAUTHENTICATED responses under AUTH_MODE=oidc. */
    readonly loginUrl?: string;
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly errorPath?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Resolve the API base URL.
 *
 * In the browser we assume the same origin (the Fastify-replacement server
 * in `apps/server/src/index.ts` serves both the SPA and `/api/*`). Tests can
 * pass an explicit base via `setApiBase`.
 */
let apiBase = '';
export function setApiBase(base: string): void {
  apiBase = base.replace(/\/+$/, '');
}
export function getApiBase(): string {
  return apiBase;
}

/**
 * Handle a 401 UNAUTHENTICATED response by sending the browser through the
 * login round-trip (AUTH_MODE=oidc). The current SPA location is carried in
 * `?redirect=` so the post-login callback restores it. Returns true when a
 * navigation was triggered (callers should stop processing).
 *
 * Exposed for unit tests via the `navigate` seam.
 */
export function redirectToLogin(
  loginUrl: string,
  navigate: (url: string) => void = (url) => {
    window.location.assign(url);
  },
  currentPath?: string,
): boolean {
  if (!loginUrl.startsWith('/') || loginUrl.startsWith('//')) return false;
  const here =
    currentPath ??
    (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/');
  const target =
    here !== '/' && here.startsWith('/') && !here.startsWith('//')
      ? `${loginUrl}?redirect=${encodeURIComponent(here)}`
      : loginUrl;
  navigate(target);
  return true;
}

async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = text ? (JSON.parse(text) as ApiErrorBody) : null;
    } catch {
      body = null;
    }
    const code = body?.error?.code ?? 'HTTP_ERROR';
    const message = body?.error?.message ?? `HTTP ${res.status}`;
    // Session expired / never logged in under AUTH_MODE=oidc: bounce the
    // whole document through /auth/login rather than surfacing a dead-end
    // error. Guarded so non-browser (test) environments just throw.
    if (res.status === 401 && code === 'UNAUTHENTICATED' && typeof window !== 'undefined') {
      const loginUrl = body?.error?.loginUrl ?? '/auth/login';
      redirectToLogin(loginUrl);
    }
    throw new ApiError(res.status, code, message, body?.error?.path);
  }
  return JSON.parse(text) as T;
}

/** Fetch the full vault tree. */
export async function fetchTree(signal?: AbortSignal): Promise<TreeResponse> {
  const init: RequestInit = {
    headers: { accept: 'application/json' },
    ...(signal ? { signal } : {}),
  };
  const res = await fetch(`${apiBase}/api/tree`, init);
  return readJson<TreeResponse>(res);
}

/** Fetch one file by vault-relative path. */
export async function fetchFile(filePath: string, signal?: AbortSignal): Promise<FilePayload> {
  const init: RequestInit = {
    headers: { accept: 'application/json' },
    ...(signal ? { signal } : {}),
  };
  const url = `${apiBase}/api/file?path=${encodeURIComponent(filePath)}`;
  const res = await fetch(url, init);
  return readJson<FilePayload>(res);
}

/* ── Unknown-file previews (US-08 fallback viewer) ────────────────────── */

import type { UnknownPreviewPayload } from './components/UnknownViewer.js';

export type { UnknownPreviewPayload } from './components/UnknownViewer.js';

/** Union of everything the content pane can be asked to render. */
export type AnyFilePayload = FilePayload | UnknownPreviewPayload;

/**
 * Extensions with a dedicated viewer served by `/api/file` (markdown,
 * canvas, excalidraw). Everything else goes through `/api/preview`, whose
 * server-side sniffing classifies text vs image vs pdf vs svg vs binary —
 * `/api/file` would UTF-8-decode a PNG into mojibake.
 */
export function hasDedicatedViewer(filePath: string): boolean {
  const lower = filePath.toLowerCase();
  return (
    lower.endsWith('.md') ||
    lower.endsWith('.markdown') ||
    lower.endsWith('.canvas') ||
    lower.endsWith('.excalidraw')
  );
}

/** Fetch the typed unknown-file preview payload for one path. */
export async function fetchPreview(
  filePath: string,
  signal?: AbortSignal,
): Promise<UnknownPreviewPayload> {
  const init: RequestInit = {
    headers: { accept: 'application/json' },
    ...(signal ? { signal } : {}),
  };
  const url = `${apiBase}/api/preview?path=${encodeURIComponent(filePath)}`;
  const res = await fetch(url, init);
  return readJson<UnknownPreviewPayload>(res);
}

/**
 * Fetch whatever payload the content pane should render for `filePath`:
 * `/api/file` for markdown/canvas/excalidraw, `/api/preview` otherwise.
 */
export async function fetchPayloadFor(
  filePath: string,
  signal?: AbortSignal,
): Promise<AnyFilePayload> {
  return hasDedicatedViewer(filePath)
    ? fetchFile(filePath, signal)
    : fetchPreview(filePath, signal);
}

/* ── Search / backlinks / plugins (sidebar panes) ─────────────────────── */

export interface SearchHit {
  readonly path: string;
  /** 1-based line number of the first match in the raw file (FR-12.1). */
  readonly line: number;
  readonly snippet: string;
  readonly score: number;
}

export interface SearchResponse {
  readonly query: string;
  readonly total: number;
  readonly hits: readonly SearchHit[];
}

/* ── Workspace layout (FR-8.4) ────────────────────────────────────────── */

export interface WorkspaceResponse {
  /** Vault-relative paths of files Obsidian last had open (layout order). */
  readonly openFiles: readonly string[];
  readonly activeFile: string | null;
}

/** The vault's `.obsidian/workspace.json` layout — used to seed tabs on
 *  first load when sessionStorage has no persisted state (FR-8.4). */
export async function fetchWorkspace(signal?: AbortSignal): Promise<WorkspaceResponse> {
  const init: RequestInit = {
    headers: { accept: 'application/json' },
    ...(signal ? { signal } : {}),
  };
  const res = await fetch(`${apiBase}/api/workspace`, init);
  return readJson<WorkspaceResponse>(res);
}

/** Full-text search. Empty queries are rejected server-side (400). */
export async function fetchSearch(query: string, signal?: AbortSignal): Promise<SearchResponse> {
  const init: RequestInit = {
    headers: { accept: 'application/json' },
    ...(signal ? { signal } : {}),
  };
  const res = await fetch(`${apiBase}/api/search?q=${encodeURIComponent(query)}`, init);
  return readJson<SearchResponse>(res);
}

export interface BacklinkMention {
  readonly sourcePath: string;
  readonly snippet: string;
  readonly offset: number;
  readonly kind: 'linked' | 'unlinked';
}

export interface OutlineHeading {
  readonly text: string;
  readonly level: number;
  readonly slug: string;
  readonly offset: number;
}

export interface BacklinksResponse {
  readonly path: string;
  readonly linked: readonly BacklinkMention[];
  readonly unlinked: readonly BacklinkMention[];
  readonly outline: readonly OutlineHeading[];
}

/** Backlinks + outline for one note. */
export async function fetchBacklinks(
  filePath: string,
  signal?: AbortSignal,
): Promise<BacklinksResponse> {
  const init: RequestInit = {
    headers: { accept: 'application/json' },
    ...(signal ? { signal } : {}),
  };
  const res = await fetch(`${apiBase}/api/backlinks?path=${encodeURIComponent(filePath)}`, init);
  return readJson<BacklinksResponse>(res);
}

export interface PluginMatrixRow {
  readonly id: string;
  readonly name: string;
  readonly status: string;
  readonly renderer: string | null;
  readonly notes: string;
  readonly homepage: string | null;
  readonly installed: boolean;
}

export interface PluginsResponse {
  readonly rows: readonly PluginMatrixRow[];
  readonly installedCount: number;
}

/* ── Identity (FR-10.4) ──────────────────────────────────────────────── */

export interface AuthUser {
  readonly sub: string;
  readonly preferredUsername?: string | null;
  readonly email?: string | null;
  readonly name?: string | null;
}

/**
 * Probe GET /auth/me. Returns the identity under oidc/proxy modes, or null
 * when unauthenticated / open mode / any failure. Deliberately does NOT go
 * through `readJson` — a 401 here is an expected answer, not a reason to
 * bounce the document to /auth/login.
 */
export async function fetchAuthIdentity(signal?: AbortSignal): Promise<AuthUser | null> {
  try {
    const init: RequestInit = {
      headers: { accept: 'application/json' },
      ...(signal ? { signal } : {}),
    };
    const res = await fetch(`${apiBase}/auth/me`, init);
    if (!res.ok) return null;
    const body = (await res.json()) as { user?: AuthUser };
    return body.user ?? null;
  } catch {
    return null;
  }
}

/** Best human-readable label for an identity. */
export function displayName(user: AuthUser): string {
  return user.name ?? user.preferredUsername ?? user.email ?? user.sub;
}

/** POST /auth/logout. Best-effort; the caller reloads the document after. */
export async function postLogout(): Promise<void> {
  await fetch(`${apiBase}/auth/logout`, {
    method: 'POST',
    headers: { accept: 'application/json' },
  }).catch(() => undefined);
}

/** Plugin support matrix for the vault. */
export async function fetchPlugins(signal?: AbortSignal): Promise<PluginsResponse> {
  const init: RequestInit = {
    headers: { accept: 'application/json' },
    ...(signal ? { signal } : {}),
  };
  const res = await fetch(`${apiBase}/api/plugins`, init);
  return readJson<PluginsResponse>(res);
}

/**
 * Walk a tree, returning a flat list of files (no directories). Used by the
 * Shell's StatusBar to show the vault file count.
 */
export function flattenFiles(nodes: readonly TreeNode[]): readonly TreeNode[] {
  const out: TreeNode[] = [];
  const walk = (ns: readonly TreeNode[]): void => {
    for (const n of ns) {
      if (n.kind === 'file') out.push(n);
      else if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}
